import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flag, X, Check } from 'lucide-react';
import { api, type ScoreCellField } from '../lib/api';
import { cn } from '../lib/cn';

interface Props {
  matchId: string;
  compId: string;
  gameNum: number;
  playerKey: string;
  field: ScoreCellField | '*';
  size?: 'xs' | 'sm';
  className?: string;
}

// Tiny "this score looks wrong" button. Open popover for an optional note,
// posts a ScoreFlagRecord. Members/leaders use this to surface OCR errors
// without being able to edit scores themselves.
export function ScoreFlagButton({ matchId, compId, gameNum, playerKey, field, size = 'xs', className }: Props) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [done, setDone] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => api.submitScoreFlag(matchId, compId, { gameNum, playerKey, field, note: note.trim() || undefined }),
    onSuccess: () => {
      setDone(true);
      setNote('');
      queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
      setTimeout(() => { setOpen(false); setDone(false); }, 1200);
    },
  });

  const iconSize = size === 'sm' ? 12 : 10;

  return (
    <span className={cn('relative inline-block', className)}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); setDone(false); mutation.reset(); }}
        title="Flag this score as incorrect"
        className={cn(
          'rounded p-0.5 transition-colors',
          open ? 'bg-amber-500/30 text-amber-200' : 'text-iron-500 hover:bg-iron-800 hover:text-amber-300',
        )}
      >
        <Flag size={iconSize} />
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute right-0 top-full z-30 mt-1 w-64 rounded border border-amber-500/40 bg-iron-950 p-2 shadow-2xl"
        >
          {done ? (
            <div className="flex items-center gap-2 px-1 py-2 text-xs text-emerald-300">
              <Check size={14} /> Flag raised — admins notified.
            </div>
          ) : (
            <>
              <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                Flag {field === '*' ? 'whole row' : field} for {playerKey}
              </div>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value.slice(0, 500))}
                placeholder="Optional — what's wrong? (e.g. kills should be 14)"
                rows={2}
                className="mb-1.5 w-full resize-none rounded border border-iron-700 bg-iron-900 px-1.5 py-1 text-xs text-iron-100 placeholder:text-iron-500 focus:border-amber-500 focus:outline-none"
              />
              {mutation.isError && (
                <div className="mb-1.5 text-[10px] text-red-300">{(mutation.error as Error).message}</div>
              )}
              <div className="flex items-center justify-end gap-1">
                <button
                  type="button"
                  onClick={() => { setOpen(false); setNote(''); }}
                  className="rounded px-2 py-0.5 text-[10px] text-iron-400 hover:text-iron-200"
                >
                  <X size={10} className="inline" /> Cancel
                </button>
                <button
                  type="button"
                  onClick={() => mutation.mutate()}
                  disabled={mutation.isPending}
                  className="rounded bg-amber-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-amber-500 disabled:opacity-50"
                >
                  {mutation.isPending ? 'Sending…' : 'Raise flag'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </span>
  );
}
